import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Grid } from '@mui/material';
import { Feedback, SupportAgent, Visibility, RateReview, Create, List } from '@mui/icons-material';
import FeedbackForm from './FeedbackForm';
import CreateTicket from './CreateTicket';
import ViewTickets from './ViewTickets';

const FeedbackTicketHub = () => {
    const [selectedTab, setSelectedTab] = useState(null);

    const tabs = [
        {
            id: 'feedback',
            title: 'Feedback',
            description: 'Share your thoughts and rate your experience with SmartGrades',
            icon: <RateReview sx={{ fontSize: 40, color: '#1976d2' }} />,
            component: <FeedbackForm onBack={() => setSelectedTab(null)} />
        },
        {
            id: 'create-ticket',
            title: 'Create a Ticket',
            description: 'Need help? Submit a support ticket and our team will assist you',
            icon: <Create sx={{ fontSize: 40, color: '#2e7d32' }} />,
            component: <CreateTicket onBack={() => setSelectedTab(null)} />
        },
        {
            id: 'view-tickets',
            title: 'View Tickets',
            description: 'Check the status of your support tickets and track progress',
            icon: <List sx={{ fontSize: 40, color: '#ed6c02' }} />,
            component: <ViewTickets onBack={() => setSelectedTab(null)} />
        }
    ];

    if (selectedTab) {
        return selectedTab;
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <SupportAgent sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    Feedback & Support Center
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, margin: '0 auto' }}>
                    We value your feedback and are here to help. Choose an option below to get started.
                </Typography>
            </Box>

            {/* Tab Cards */}
            <Grid container spacing={3} justifyContent="center">
                {tabs.map((tab) => (
                    <Grid item xs={12} sm={6} md={4} key={tab.id}>
                        <Card
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    '& .tab-icon': {
                                        transform: 'scale(1.1)',
                                    }
                                },
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px'
                            }}
                            onClick={() => setSelectedTab(tab.component)}
                        >
                            <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {/* Icon */}
                                <Box sx={{ mb: 2 }}>
                                    <Box 
                                        className="tab-icon"
                                        sx={{ 
                                            transition: 'transform 0.3s ease',
                                            display: 'inline-block'
                                        }}
                                    >
                                        {tab.icon}
                                    </Box>
                                </Box>

                                {/* Title */}
                                <Typography 
                                    variant="h6" 
                                    gutterBottom 
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#1a1a1a',
                                        mb: 1.5
                                    }}
                                >
                                    {tab.title}
                                </Typography>

                                {/* Description */}
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                        lineHeight: 1.6,
                                        flexGrow: 1,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {tab.description}
                                </Typography>

                                {/* Hover indicator */}
                                <Box 
                                    sx={{ 
                                        mt: 2,
                                        pt: 1,
                                        borderTop: '1px solid #f0f0f0'
                                    }}
                                >
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: '#1976d2',
                                            fontWeight: 500
                                        }}
                                    >
                                        Click to continue →
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Footer info */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Need immediate assistance? Contact us at{' '}
                    <Typography component="span" sx={{ color: '#1976d2', fontWeight: 500 }}>
                        support@gradely.ca
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );
};

export default FeedbackTicketHub;